/**
 * One sheet metal part found inside a dropped STEP file.
 *
 * The row carries what a list needs to show and what a query needs to filter
 * on. Everything geometric is re-derived from the STEP in the browser, and
 * everything decided is kept in the `config` blob — see SheetMetalPartConfig in
 * @prism/sheet-metal-engine. Splitting it that way means a part is never stale:
 * improve the unfold and every part picks it up, because only the decisions
 * were saved.
 */
export enum SheetMetalPartStatus {
    /** Analysed, but bends still need a gauge reference. */
    NeedsGauge = 'NeedsGauge',
    /** Gauged, but no forming order yet. */
    NeedsSequence = 'NeedsSequence',
    /** Fully configured — will produce a laser and a press brake task. */
    Ready = 'Ready',
    /** No bends at all. Laser only; nothing to configure. */
    FlatOnly = 'FlatOnly',
    /** The unfold could not be trusted. Needs a person to look at it. */
    Failed = 'Failed',
}

/** Summary of one bend, kept on the row so a list can show it without the STEP. */
export interface SheetMetalBendSummary {
    index: number;
    angleDeg: number;
    innerRadiusIn: number;
    bendAllowanceIn: number;
    lengthIn: number;
    /** True once this bend has a gauge reference, natural edge or tab. */
    gauged: boolean;
    /** Set when the gauge reference is a tab added to the blank. */
    usesTabs: boolean;
    /** Distance from the mould line to the gauge face, once chosen. */
    gaugeDistanceIn: number | null;
}

export interface SheetMetalPart {
    id: string;
    jobId: string;
    sourceFileId: string;
    /** Which solid within the STEP this came from — assemblies hold many. */
    solidIndex: number;
    name: string;

    /**
     * How many to make. Assemblies repeat the same part, so identical solids
     * are collapsed on import and this counts them.
     */
    quantity: number;
    /** Fingerprint that drove the collapse, kept so it can be explained. */
    duplicateSignature: string | null;
    /** Names of the solids folded into this row. */
    mergedSiblings: string[];

    thicknessIn: number;
    kFactor: number;
    bendCount: number;
    bends: SheetMetalBendSummary[];
    /** Blank extent, for picking sheet stock and for the laser task. */
    blankLengthIn: number;
    blankWidthIn: number;

    status: SheetMetalPartStatus;
    /** Why the unfold was rejected, when status is Failed. */
    error: string | null;

    /** Decisions only — gauge choices, tabs, forming order. JSON in storage. */
    configFileId: string | null;
    /** Cut outline with any gauge tabs merged in. Feeds the laser task. */
    dxfFileId: string | null;
    /**
     * Self-contained payload the press brake station renders in 3D. Baked at
     * convert time so the station never needs the STEP or the engine's unfold.
     */
    bendConfigFileId: string | null;

    createdOn: Date;
    createdById: string;
    updatedOn: Date;
    updatedById: string;
}
